// src/components/ProductPage/ProductImageGallery.js
import React from 'react';
import { Box, Paper, IconButton } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import { motion } from 'framer-motion';

const ProductImageGallery = ({ 
  images, 
  currentImageIndex, 
  setCurrentImageIndex
}) => {
  const nextImage = () => {
    if (images.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }
  };

  const prevImage = () => {
    if (images.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    }
  };

  return (
    <Box>
      {/* Main Image Gallery */}
      <Paper 
        elevation={0}
        sx={{ 
          position: 'relative',
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: '#f8f8f8',
          mb: 2
        }}
      >
        {/* Main Image */}
        <Box sx={{ 
          position: 'relative',
          height: { xs: 400, md: 600 },
          overflow: 'hidden'
        }}>
          <motion.img
            key={currentImageIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            src={images[currentImageIndex]}
            alt={`صورة ${currentImageIndex + 1}`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            }}
          />

          {/* Navigation Arrows */}
          {images.length > 1 && (
            <>
              <IconButton
                onClick={prevImage}
                sx={{
                  position: 'absolute',
                  left: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: '#0f3662',
                  color: 'white',
                  '&:hover': { bgcolor: 'white', color: '#0f3662' },
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}
              >
                <ArrowBackIos />
              </IconButton>

              <IconButton
                onClick={nextImage}
                sx={{
                  position: 'absolute',
                  right: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: '#0f3662',
                  color: 'white',
                  '&:hover': { bgcolor: 'white', color: '#0f3662' },
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}
              >
                <ArrowForwardIos />
              </IconButton>
            </>
          )}

          {/* Image Dots */}
          {images.length > 1 && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: 1,
                flexDirection: 'row-reverse'
              }}
            >
              {images.map((_, index) => (
                <Box
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: index === currentImageIndex ? '#fff' : 'rgba(255,255,255,0.5)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': { bgcolor: '#fff' }
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Thumbnail Gallery */}
      {images.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            pb: 1,
            flexDirection: 'row-reverse', // RTL support
            '&::-webkit-scrollbar': {
              height: 4
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: '#f1f1f1',
              borderRadius: 2
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: '#c1c1c1',
              borderRadius: 2,
              '&:hover': {
                bgcolor: '#a8a8a8'
              }
            }
          }}
        >
          {images.map((image, index) => (
            <Paper
              key={index}
              elevation={currentImageIndex === index ? 2 : 0}
              onClick={() => setCurrentImageIndex(index)}
              sx={{
                minWidth: { xs: 60, md: 80 },
                height: { xs: 60, md: 80 },
                borderRadius: 2,
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                border: currentImageIndex === index ? '2px solid #0f3662' : '2px solid transparent',
                opacity: currentImageIndex === index ? 1 : 0.7,
                '&:hover': {
                  opacity: 1,
                  elevation: 2
                }
              }}
            >
              <img
                src={image}
                alt={`صورة مصغرة ${index + 1}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ProductImageGallery;
